import { inject, Injectable } from '@angular/core';
import { Pagination } from '@core/models/pagination.model';
import { StateContext, Action } from '@ngxs/store';
import { TableActions } from '../table.actions';
import { TableStateModel } from '../table.state.model';
import { ReportService } from '@core/services/report.service';
import { AccountManagementService } from '@core/services/account-management.service';

@Injectable({
  providedIn: 'root',
})
export class ReportsHandler {
  reportService = inject(ReportService);
  accountService = inject(AccountManagementService);
  // Active client
  handleActiveClientLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    this.reportService
      .getActiveAndInactiveAccountList(
        { ...params },
        'active',
        'get-active-account-list'
      )
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new TableActions.LoadTableDataSuccess({
              tableKey: 'activeClients',
              data: response.data?.data,
              totalCount: response.data?.total_count,
            })
          );
        },
        error: (error) => {
          ctx.dispatch(
            new TableActions.LoadTableDataFailure({
              tableKey: 'activeClients',
              error: error.message,
            })
          );
        },
      });
  }

  @Action(TableActions.FilterActiveClientsList)
  filterActiveClientsList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterActiveClientsList
  ) {
    this.reportService
      .getActiveAndInactiveAccountList(
        undefined,
        action.payload.type,
        'get-active-account-list',
        action.payload.filter
      )
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new TableActions.LoadTableDataSuccess({
              tableKey: action.payload.type == 'active' ? 'activeClients' : 'inactiveClients',
              data: response.data?.data,
              totalCount: response.data?.total_count,
            })
          );
        },
        error: (error) => {
          ctx.dispatch(
            new TableActions.LoadTableDataFailure({
              tableKey: action.payload.type == 'active' ? 'activeClients' : 'inactiveClients',
              error: error.message,
            })
          );
        },
      });

  }

  // Inactive client
  handleInactiveClientsLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    this.reportService
      .getActiveAndInactiveAccountList(
        { ...params },
        'inactive',
        'get-inactive-account-list'
      )
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new TableActions.LoadTableDataSuccess({
              tableKey: 'inactiveClients',
              data: response.data?.data,
              totalCount: response.data?.total_count,
            })
          );
        },
        error: (error) => {
          ctx.dispatch(
            new TableActions.LoadTableDataFailure({
              tableKey: 'inactiveClients',
              error: error.message,
            })
          );
        },
      });
  }
  // Client with fund
  handleClientWFLoad(ctx: StateContext<TableStateModel>, params: Pagination) {
    this.reportService.getFundedAccountList({ ...params }).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'clientWF',
            data: response.data?.data,
            totalCount: response.data?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'clientWF',
            error: error.message,
          })
        );
      },
    });
  }

  @Action(TableActions.FilterFundedClientsList)
  filterFundedClientsList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterFundedClientsList
  ) {
    this.reportService
      .getFundedAccountList(
        undefined,
        action.payload.filter
      )
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new TableActions.LoadTableDataSuccess({
              tableKey: 'clientWF',
              data: response.data?.data,
              totalCount: response.data?.total_count,
            })
          );
        },
        error: (error) => {
          ctx.dispatch(
            new TableActions.LoadTableDataFailure({
              tableKey: 'clientWF',
              error: error.message,
            })
          );
        },
      });

  }

  // Client with portfolio fund
  handleClientWPFLoad(ctx: StateContext<TableStateModel>, params: Pagination) {
    this.reportService.getPortfolioFundedAccountList({ ...params }).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'clientWPF',
            data: response.data?.data,
            totalCount: response.data?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'clientWPF',
            error: error.message,
          })
        );
      },
    });
  }

  @Action(TableActions.FilterPortfolioFundedClientsList)
  filterPortfolioFundedClientsList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterPortfolioFundedClientsList
  ) {
    this.reportService
      .getPortfolioFundedAccountList(
        undefined,
        action.payload.filter
      )
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new TableActions.LoadTableDataSuccess({
              tableKey: 'clientWPF',
              data: response.data?.data,
              totalCount: response.data?.total_count,
            })
          );
        },
        error: (error) => {
          ctx.dispatch(
            new TableActions.LoadTableDataFailure({
              tableKey: 'clientWPF',
              error: error.message,
            })
          );
        },
      });

  }

  // portfolio
  handlePortfolioLoad(ctx: StateContext<TableStateModel>, params: Pagination) {
    this.reportService.getGlobalPortfolioList({ ...params }).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'portfolio',
            data: response.data?.portfolio_balance,
            totalCount: response.data?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'portfolio',
            error: error.message,
          })
        );
      },
    });
  }

  @Action(TableActions.FilterPortfolioReportList)
  filterPortfolioReportList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterPortfolioReportList
  ) {
    this.reportService
      .getGlobalPortfolioList(
        undefined,
        action.payload.filter
      )
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new TableActions.LoadTableDataSuccess({
              tableKey: 'portfolio',
              data: response.data?.portfolio_balance,
              totalCount: response.data?.total_count,
            })
          );
        },
        error: (error) => {
          ctx.dispatch(
            new TableActions.LoadTableDataFailure({
              tableKey: 'portfolio',
              error: error.message,
            })
          );
        },
      });

  }
  // General
  handleGeneralLoad(ctx: StateContext<TableStateModel>, params: Pagination) {
    this.accountService.viewAccountList({ ...params }).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'general',
            data: response.data?.data,
            totalCount: response.data?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'general',
            error: error.message,
          })
        );
      },
    });
  }


  // MNMMF Statements
  handleMnmmfStatementsLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    this.reportService
      .getMNMMFStatements({ ...params })
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new TableActions.LoadTableDataSuccess({
              tableKey: 'mnmmfStatements',
              data: response.data?.list,
              totalCount: response.data?.count,
            })
          );
        },
        error: (error) => {
          ctx.dispatch(
            new TableActions.LoadTableDataFailure({
              tableKey: 'mnmmfStatements',
              error: error.message,
            })
          );
        },
      });
  }

  @Action(TableActions.FilterMNMMFStatementsList)
  filterMNMMFStatementsList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterMNMMFStatementsList
  ) {
    this.reportService
      .getMNMMFStatements(
        undefined,
        action.payload.filter
      )
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new TableActions.LoadTableDataSuccess({
              tableKey: 'mnmmfStatements',
              data: response.data?.list,
              totalCount: response.data?.count,
            })
          );
        },
        error: (error) => {
          ctx.dispatch(
            new TableActions.LoadTableDataFailure({
              tableKey: 'mnmmfStatements',
              error: error.message,
            })
          );
        },
      });

  }
}
